import TableContainer from "components/Common/TableContainer"
import React, { useEffect, useMemo, useState, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import { withRouter } from "react-router-dom"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import * as url from "../../helpers/url_helper"
import { API_ENCRYPTION_MODES } from "helpers/api_constants"

import { Name, ShortDescription } from "./merchantGroupCol"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

import {
  getMerchantGroup as onGetMerchantGroup,
  clearMerchantGroup,
  deleteMerchantGroup,
} from "store/merchant-group/actions"

//redux
import AccessControlWrapper from "components/Common/PermissionButton"
import { useDispatch, useSelector } from "react-redux"
import { PageTitle } from "constants/ui"
import IconButton from "components/Common/IconButton"
import CButton from "components/Common/CButton"
import ConfirmButton from "components/Common/ConfirmButton"
import { getFirstTabPermission } from "helpers/tab_helper"
import { calculatePageAfterAction, calculateStartIndex } from "helpers/pagination_helper"

const MerchantGroupList = props => {
  //meta title
  document.title = PageTitle["MERCHANT_GROUP_LIST"]

  const dispatch = useDispatch()
  const { history } = props
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)

  const { data, state, userMenu } = useSelector(state => ({
    data: state.merchantGroup,
    state: state,
    userMenu: state.userMenu?.userMenu || [],
  }))

  const firstTabPermission = getFirstTabPermission("MERCHANT_GROUP_DETAIL", userMenu)

  const columns = useMemo(
    () => [
      {
        Header: "Logo",
        accessor: "logo",
        disableFilters: true,
        filterable: true,

        accessor: cellProps => (
          <>
            {!cellProps.logo ? (
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle">
                  {cellProps.legalEntityName.charAt(0)}
                </span>
              </div>
            ) : (
              <div>
                <img
                  className="rounded-circle avatar-xs"
                  src={cellProps.logo}
                  alt=""
                />
              </div>
            )}
          </>
        ),
      },
      {
        Header: "Display Name",
        accessor: "displayName",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <ShortDescription {...cellProps} />
        },
      },
      {
        Header: "Legal Entity Name",
        accessor: "legalEntityName",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <Name {...cellProps} />
        },
      },

      {
        Header: "Legal Entity Address",
        accessor: "legalEntityAddress",
        filterable: true,
        disableFilters: true,
        Cell: cellProps => {
          return <ShortDescription {...cellProps} />
        },
      },

      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <div className="d-flex gap-2">
              <AccessControlWrapper
                actionType={firstTabPermission?.actionType || "get"}
                contextUri={firstTabPermission?.contextUri || url.GET_MERCHANT_GROUP}
                history={history}
              >
                <IconButton.View
                  onClick={() => {
                    const merchantGroupID = cellProps.row.original.merchantGroupID
                    history.push(`/merchant-groups/${merchantGroupID}`)
                  }}
                />
              </AccessControlWrapper>

              <ConfirmButton
                onConfirm={() =>
                  handleDeleteMerchantGroup(
                    cellProps.row.original.merchantGroupID
                  )
                }
                message="Are you sure you want to delete this Merchant Group? This action cannot be undone."
                renderButton={<IconButton.Delete />}
                contextUri={url.MANAGE_MERCHANT_GROUP}
                actionType="post"
              ></ConfirmButton>
            </div>
          )
        },
      },
    ],
    []
  )

  // Load fresh merchant groups data when component mounts
  useEffect(() => {
    dispatch(
      onGetMerchantGroup({
        headers: {
          "input-parameters": {},
        },
        pagination: { startIndex: 0 },
      })
    )
  }, [dispatch])

  // Clear merchant groups and search when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearMerchantGroup())
    }
  }, [dispatch])

  // add row click function
  const rowClick = (e, row) => {
    const {
      original: { merchantGroupID },
    } = row
    history.push({
      pathname: `/merchant-groups/${merchantGroupID}`,
    })
  }

  function handleAddMerchantGroupClick() {
    history.push("/merchant-groups/add-merchant-group")
  }

  const dispatchSearch = useCallback((value) => {
    setPage(1)
    dispatch(
      onGetMerchantGroup({
        headers: {
          "input-parameters": { keyword: value || "" },
        },
        pagination: { startIndex: 0 },
      })
    )
  }, [dispatch])

  const [debouncedSearch] = useDebounce(dispatchSearch)

  const handleSearchChange = e => {
    const { value } = e.target
    setSearch(value)
    debouncedSearch(value)
  }

  const handlePageChange = page => {
    const startIndex = (page - 1) * 10 // Assuming page size is 10
    setPage(page)
    dispatch(
      onGetMerchantGroup({
        headers: {
          "input-parameters": { keyword: search || "" },
        },
        pagination: { startIndex },
      })
    )
  }

  const handleRefresh = () => {
    dispatch(
      onGetMerchantGroup({
        headers: {
          "input-parameters": { keyword: search || "" },
        },
        pagination: { startIndex: (page - 1) * 10 },
      })
    )
  }

  const handleDeleteMerchantGroup = merchantGroupID => {
    const targetPage = calculatePageAfterAction(page, data.merchantGroups?.length || 0)
    dispatch(
      deleteMerchantGroup(
        {
          merchantGroupID: merchantGroupID,
          actionType: "delete",
        },
        {
          refreshPagination: {
            startIndex: calculateStartIndex(targetPage),
            keyword: search || ""
          }
        }
      )
    )
    setPage(targetPage)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs
            title={PageTitle["MERCHANT_GROUP_LIST"]}
            breadcrumbItem={PageTitle["MERCHANT_GROUP_LIST"]}
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row>
                    <Col md="6">
                      <div>
                        <input
                          type="text"
                          placeholder="search merchant groups..."
                          onChange={handleSearchChange}
                          className="form-control w-50 rounded-pill"
                        />
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="text-sm-end">
                        <AccessControlWrapper
                          actionType={"post"}
                          contextUri={
                            url.MANAGE_MERCHANT
                          }
                          history={history}
                        >
                          <CButton.Primary
                            icon="mdi mdi-plus-circle-outline"
                            text="Add Merchant Group"
                            className="mb-2 me-2"
                            onClick={handleAddMerchantGroupClick}
                          />
                        </AccessControlWrapper>
                      </div>
                    </Col>
                  </Row>

                  {/* Delete Error Alert */}
                  {data?.deleteError?.message && (
                    <Row className="mb-3">
                      <Col lg="12">
                        <div className="alert alert-danger" role="alert">
                          {data.deleteError.message}
                        </div>
                      </Col>
                    </Row>
                  )}

                  <TableContainer
                    columns={columns}
                    data={data?.error?.message ? [] : data.merchantGroups}
                    isGlobalFilter={false}
                    isAddUserList={true}
                    rowClick={rowClick}
                    protectedRowClick={true}
                    contextUri={url.GET_MERCHANT_GROUP}
                    actionType="get"
                    handleAddButtonClick={handleAddMerchantGroupClick}
                    customPageSize={10}
                    className="custom-header-css"
                    columnFilter={false}
                    changePageSize={false}
                    isAddButtonText="Add Merchant Group"
                    onPageChange={handlePageChange}
                    currentPage={page}
                    hasNextPage={!data?.error?.message && data.merchantGroups?.length >= 10}
                    onRefresh={handleRefresh}
                    refreshContextUri={url.GET_MERCHANT_GROUP}
                    refreshActionType="get"
                    history={history}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(MerchantGroupList)
